import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../api';

/** Owns the Dashboard Overview tab's stats/analytics/refund-stats and its polling. */
export default function useDashboardOverview({ activeTab, setActiveTab, inventory }) {
  const [dashboardStats, setDashboardStats] = useState(null);
  const [dashboardAnalytics, setDashboardAnalytics] = useState(null);
  const [analyticsTimeRange, setAnalyticsTimeRange] = useState('all');
  const [loading, setLoading] = useState(true);
  const [refundStats, setRefundStats] = useState(null);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const baseUrl = API_BASE_URL;

      const [statsRes, analyticsRes] = await Promise.all([
        fetch(`${baseUrl}/api/admin/dashboard/stats?period=${analyticsTimeRange}`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        }),
        fetch(`${baseUrl}/api/admin/dashboard/analytics?period=${analyticsTimeRange}`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        }),
      ]);

      if (statsRes.ok) {
        const stats = await statsRes.json();
        setDashboardStats(stats);
      }

      if (analyticsRes.ok) {
        const analytics = await analyticsRes.json();
        setDashboardAnalytics(analytics);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch order statistics for dashboard
  const fetchOrderStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/orders/stats/summary`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      }
    } catch (error) {
      console.error('Error fetching order stats:', error);
    }
    return null;
  };

  // Fetch refund statistics
  const fetchRefundStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/refunds/stats`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const stats = await response.json();
        setRefundStats(stats);
      }
    } catch (error) {
      console.error('Error fetching refund stats:', error);
    }
  };

  // Real-time updates for dashboard
  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchRefundStats();
      fetchDashboardStats();
      const interval = setInterval(() => {
        fetchDashboardStats();
        fetchOrderStats();
        fetchRefundStats();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [activeTab, analyticsTimeRange]);

  // Fetch refund stats when refunds tab is active (the list itself is owned
  // by the standalone <RefundManagement /> component, not this file)
  useEffect(() => {
    if (activeTab === 'refunds') {
      fetchRefundStats();
    }
  }, [activeTab]);

  const handleViewLowStockDetails = () => {
    setActiveTab('inventory');
    inventory.viewLowStockDetails();
  };

  return {
    dashboardStats, dashboardAnalytics, analyticsTimeRange, setAnalyticsTimeRange,
    loading, refundStats, fetchDashboardStats, handleViewLowStockDetails,
  };
}
